// ModalFooter — Presentational footer action bar for the EditTripModal: the
// top-bordered row holding the "Cancel" button and the "Save Changes" button,
// the latter switching to a spinner + "Saving..." label and a disabled,
// gray, not-allowed state while a save is in flight.
//
// Extracted verbatim from `EditTripModal.js` (the "Action Buttons" block) as
// part of the behavior-preserving refactor (issue #240). In the source the
// Cancel button called `onClose` and the Save button called `handleSave`, with
// the `isSaving` flag driving the disabled state, the background color, the
// cursor, and the spinner/label swap; those arrive here as the `isSaving`,
// `onClose`, and `onSave` props so the component stays render-only. Wrapped in
// React.memo. Button labels ("Cancel", "Save Changes", "Saving..."), the inline
// spinner markup + `spin` animation, the inline hover handlers, the Edit3 icon
// (lucide-react), and styles are identical to the source.
//
// Props:
//   isSaving - boolean; drives the disabled state, colors, cursor, and the
//              spinner/"Saving..." vs "Save Changes" label
//   onClose  - () => void invoked by the "Cancel" button
//   onSave   - () => void invoked by the "Save Changes" button
//
// Validates: Requirements 3.3, 3.4, 1.2, 1.6

import React from 'react';
import { Edit3 } from 'lucide-react';

export const ModalFooter = React.memo(function ModalFooter({
  isSaving,
  onClose,
  onSave
}) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '12px',
      paddingTop: '16px',
      borderTop: '1px solid #e5e7eb'
    }}>
      <button
        onClick={onClose}
        style={{
          padding: '10px 20px',
          border: '1px solid #d1d5db',
          backgroundColor: 'white',
          color: '#374151',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
      >
        Cancel
      </button>
      <button
        onClick={onSave}
        disabled={isSaving}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 20px',
          backgroundColor: isSaving ? '#9ca3af' : '#059669',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: isSaving ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          if (!isSaving) e.target.style.backgroundColor = '#047857';
        }}
        onMouseLeave={(e) => {
          if (!isSaving) e.target.style.backgroundColor = '#059669';
        }}
      >
        {isSaving ? (
          <>
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid white',
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            Saving...
          </>
        ) : (
          <>
            <Edit3 size={16} />
            Save Changes
          </>
        )}
      </button>
    </div>
  );
});

export default ModalFooter;
